"use client";

import React from "react";
import { DOCXViewer as BaseDOCXViewer } from "@webuild-ai/react-ui/components";

import { findTextMatch } from "@/utils/textMatching";
import { logger } from "@/telemetry";

import {
  buildTextNodeIndex,
  collectTextNodes,
  createHighlightMark,
  findCitationNodeRange,
  scrollToHighlight,
} from "./textNodeHighlight";

export interface DOCXViewerProps {
  /** DOCX document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the DOCX */
  citationText?: string;
}

/**
 * DOCX Viewer component for Citations
 * Extends the shared DOCXViewer with citation text highlighting
 */
export const DOCXViewer: React.FC<DOCXViewerProps> = ({ documentData, citationText }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [documentText, setDocumentText] = React.useState<string>("");

  /**
   * Callback when document is rendered
   * Store the container reference and document text for highlighting
   */
  const handleRendered = React.useCallback((container: HTMLDivElement, extractedText: string) => {
    containerRef.current = container;
    setDocumentText(extractedText);
  }, []);

  // Find and highlight citation text
  React.useEffect(() => {
    if (!citationText || !documentText || !containerRef.current) return;

    logger.info("DOCX: Attempting to find and highlight", {
      citationTextLength: citationText.length,
      documentTextLength: documentText.length,
      citationPreview: citationText.substring(0, 100),
    });

    const matchResult = findTextMatch(documentText, citationText);

    logger.info("DOCX: Match result", { matchResult: String(matchResult) });

    if (matchResult) {
      // Wait a bit for docx-preview to fully render before highlighting
      setTimeout(() => {
        if (!containerRef.current) return;
        logger.info("DOCX: Starting highlight in DOM (after delay)");
        highlightTextInDOM(containerRef.current, citationText);

        // Check if marks are still there after 1 second
        setTimeout(() => {
          const marks = containerRef.current?.querySelectorAll("mark");

          logger.info("DOCX: After 1s, mark elements in DOM", { markCount: marks?.length || 0 });
        }, 1000);
      }, 500); // 500ms delay
    } else {
      logger.info("DOCX: No match found (document will display without highlighting)");
    }
  }, [citationText, documentText]);

  /**
   * Highlight matched text in the rendered DOCX DOM
   */
  const highlightTextInDOM = (container: HTMLElement, citationText: string) => {
    try {
      const textNodes = collectTextNodes(container);

      if (textNodes.length === 0) {
        logger.info("No text nodes found in DOCX container (document may still be rendering)");

        return;
      }

      const { startNodeIndex, endNodeIndex } = findCitationNodeRange(
        buildTextNodeIndex(textNodes),
        textNodes.length,
        citationText,
      );
      const overlappingNodes = textNodes.slice(startNodeIndex, endNodeIndex + 1);

      logger.info("DOCX: Highlighting nodes", {
        startNodeIndex,
        endNodeIndex,
        overlappingNodesCount: overlappingNodes.length,
        textNodesCount: textNodes.length,
      });

      let firstMark: HTMLElement | undefined;
      let highlightedCount = 0;
      let skippedCount = 0;

      overlappingNodes.forEach((node, index) => {
        const parent = node.parentElement;

        if (!parent) {
          skippedCount++;
          logger.info("DOCX: Node has no parent (skipping)", { nodeIndex: index });

          return;
        }

        const mark = createHighlightMark(node.nodeValue || "");

        try {
          parent.replaceChild(mark, node);
          highlightedCount++;
        } catch (err) {
          skippedCount++;
          logger.error(`DOCX: Failed to replace node ${index}`, err);
        }

        // Keep reference to first mark for scrolling
        firstMark ??= mark;
      });

      logger.info("DOCX: Highlight complete", { highlightedCount, skippedCount });
      scrollToHighlight(firstMark);
    } catch (err) {
      logger.error("Failed to highlight text in DOCX", err);
    }
  };

  return (
    <BaseDOCXViewer
      documentData={documentData}
      loadingLabel="Loading DOCX..."
      onError={(err) => logger.error("Failed to render DOCX", err)}
      onRendered={handleRendered}
    />
  );
};

export default DOCXViewer;
